// @ts-check

import React, { useMemo } from 'react';
import format from 'date-fns/format';
import { getCalendarSettings } from 'constants/calendarSettings';
import useTextProps from './hooks/useTextProps';
import DaySize from './DaySize';

/**
 * The WeekdayName component renders the name of a weekday.
 *
 * @param {Object} props The props for the component.
 * @param {import('actions/scene.types').CalendarShape} props.shape The shape of the calendar.
 * @param {Date} props.day The date
 * @param {number} props.index The index of the weekday.
 *
 * @returns {React.ReactElement} The rendered day component.
 */
const WeekdayName = ({ shape, day, index }) => {
  const calendarSettings = useMemo(() => getCalendarSettings(shape), [shape]);
  const dayOfWeekFormatting = format(day, shape.dayFormat || 'EEEEEE');
  const textAlign = useMemo(() => {
    /** @type {'left'|'center'|'right'} */
    let textAlign = 'right';
    if (shape.weekdayNameTextAlign) {
      textAlign = shape.weekdayNameTextAlign;
    } else if (shape.calendarType === 2) {
      textAlign = 'center';
    }
    return textAlign;
  }, [shape.calendarType, shape.weekdayNameTextAlign]);
  const header = calendarSettings.WeekdayNameShape;
  const nameTextProps = useTextProps({
    textAlign,
    shape: {
      width: Math.max(0, header.width - header.margin.left - header.margin.right),
      margin: { left: 0, right: 0 },
    },
  });
  return (
    <g transform={`translate(${index * calendarSettings.DayShape.width} 0)`}>
      <DaySize
        calendarSettings={calendarSettings}
        cellShape={header}
        showDebugGuides={shape.showWeekdayNameDebugGuides === true}
      />
      <text
        {...nameTextProps}
        x={nameTextProps.x + header.margin.left}
        y={header.height - header.margin.bottom}
        fontSize={shape.headerFontSize}
        dominantBaseline="alphabetic"
        fontWeight={shape.headerIsBold ? 'bold' : 'normal'}
        fontStyle={shape.headerIsItalic ? 'italic' : 'normal'}
        fontFamily={shape.headerFontFamily || shape.fontFamily}
        fill={shape.headerColor || shape.mainColor}
      >
        {dayOfWeekFormatting}
      </text>
    </g>
  );
};

export default WeekdayName;
